import { useState, useRef, useEffect } from "react"
import Image, { ImageProps } from "next/image"
import isBrowser from "../utils/isBrowser"
import useIntersectionObserver from "../hooks/window/useIntersectionObserver"

// Pixel GIF code adapted from https://stackoverflow.com/a/33919020/266535
const keyStr =
	"ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/="

const triplet = (e1: number, e2: number, e3: number) =>
	keyStr.charAt(e1 >> 2) +
	keyStr.charAt(((e1 & 3) << 4) | (e2 >> 4)) +
	keyStr.charAt(((e2 & 15) << 2) | (e3 >> 6)) +
	keyStr.charAt(e3 & 63)

const rgbDataURL = (r: number, g: number, b: number) =>
	`data:image/gif;base64,R0lGODlhAQABAPAA${triplet(0, r, g) +
		triplet(b, 255, 255)}/yH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==`

interface ColorShimImageProps extends ImageProps {
	rgb: [number, number, number]
	/**
	 * Function which will be called every time the image is intersected
	 */
	onIntersection?: () => void
	/**
	 * Fallback Image which can be shown
	 */
	fallbackSrc?: string
}

const ColorShimImage = ({
	src,
	fallbackSrc = "",
	rgb,
	alt = "Blurred preview",
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	onIntersection = () => {},
	className,
	...props
}: ColorShimImageProps) => {
	const [r, g, b] = rgb

	const targetRef = useRef(null)
	const [showImage, setShowImage] = useState(false)
	const isIntersecting = useIntersectionObserver(
		targetRef,
		isBrowser() ? document.querySelector("body") : null,
	)

	useEffect(() => {
		if (isIntersecting && !showImage) setShowImage(true)
	}, [isIntersecting, showImage])

	useEffect(() => {
		if (isIntersecting && onIntersection) {
			onIntersection()
		}
	}, [isIntersecting, onIntersection])

	return (
		<>
			<div ref={targetRef} className={className}>
				<Image
					src={showImage ? src : fallbackSrc || rgbDataURL(r, g, b)}
					alt={alt}
					placeholder="blur"
					blurDataURL={rgbDataURL(r, g, b)}
					{...props}
				/>
			</div>
		</>
	)
}

export default ColorShimImage
